import { type ReactNode } from 'react'

import type { HospitalListItem } from '../../api/hospitals'
import { profileServices } from '../../lib/hospitalProfile'
import { Icon } from '../Icon'

/**
 * About tab (M12-T03) — the hospital's description, accreditations, languages,
 * service capabilities, and contacts, all from existing `hospitals` columns.
 * `description_local` isn't seeded yet (a noted gap, not blocking) — we show a
 * neutral placeholder when it's absent rather than an empty panel.
 */
export function AboutTab({
  hospital,
}: {
  hospital: HospitalListItem
}): ReactNode {
  const accreditations = hospital.accreditations ?? []
  const languages = hospital.languages ?? []
  const services = profileServices(hospital.services)

  return (
    <div className="px-4 py-4">
      <p className="text-[13px] leading-relaxed text-ink-2">
        {hospital.description_local ??
          `${hospital.name} is a verified partner hospital in ${hospital.city}. A fuller description is coming soon.`}
      </p>

      {(hospital.verified || accreditations.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {hospital.verified && (
            <span className="inline-flex items-center gap-1 rounded-full border border-line-1 px-2 py-0.5 text-[11px] text-ink-2">
              <Icon name="badge" size={11} /> Verified partner
            </span>
          )}
          {accreditations.map((a) => (
            <span
              key={a}
              className="rounded-full border border-line-1 px-2 py-0.5 text-[11px] text-ink-2"
            >
              {a}
            </span>
          ))}
        </div>
      )}

      {languages.length > 0 && (
        <>
          <h3 className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-wide text-ink-3">
            Languages
          </h3>
          <p className="text-[13px] text-ink-2">{languages.join(' · ')}</p>
        </>
      )}

      {services.length > 0 && (
        <>
          <h3 className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-wide text-ink-3">
            Services
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {services.map((s) => (
              <span
                key={s.name}
                className="rounded-full border border-line-1 bg-bg-soft px-2 py-0.5 text-[11px] text-ink-2"
              >
                {s.name}
              </span>
            ))}
          </div>
        </>
      )}

      <h3 className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-wide text-ink-3">
        Contact
      </h3>
      <ul className="flex flex-col gap-1.5">
        <li>
          <a
            href={`tel:${hospital.phone}`}
            className="inline-flex items-center gap-2 text-[13px] font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="phone" size={14} /> {hospital.phone}
          </a>
        </li>
        {hospital.whatsapp && (
          <li>
            <a
              href={`https://wa.me/${hospital.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Icon name="chat" size={14} /> WhatsApp
            </a>
          </li>
        )}
        <li className="inline-flex items-center gap-2 text-[13px] text-ink-2">
          <Icon name="pin" size={14} /> {hospital.address}
        </li>
      </ul>
    </div>
  )
}
